/**
 * A job waiting for a part.
 *
 * Until now the stores knew a job was short and the job did not. The work
 * order sat in progress, its SLA clock running, while a bearing crossed a
 * continent — and when it breached, the maintenance KPI counted it as a team
 * that had been slow. That is not what happened, and a measure that cannot
 * tell the two apart teaches everybody to distrust it.
 *
 * So three things, and the third is the one that matters:
 *
 *  1. **The job says what it is waiting for** — which part, how many short,
 *     whether anybody has ordered it and when it is due.
 *  2. **It goes on hold with that reason recorded**, not with a free-text
 *     note somebody may or may not have written.
 *  3. **The time it spends held for parts comes off its SLA clock** — and
 *     ONLY that time. A job held because nobody turned up still counts. The
 *     distinction is the whole justification for pausing anything, and a
 *     pause that applied to every hold would be an excuse rather than a
 *     measurement.
 *
 * ── how a hold is recognised later ──────────────────────────────────────
 *
 * The platform records a transition's reason in the event's note. There is no
 * field on it for "why, in a way a machine can read", so the note carries a
 * marker this file owns — `[parts]` — written by `partsHoldNote` and read by
 * `isPartsHold`. A convention, stated in one place, tested in both
 * directions. The day the platform carries structured attrs on a transition,
 * this reads those instead and nothing above it changes.
 */
import { t, tn } from "../i18n";
import {
  canTransition,
  OPEN_STATUSES,
  STATUS_LABEL,
  TERMINAL_STATUSES,
  type WorkOrder,
  type WorkOrderEvent,
  type WorkOrderStatus,
} from "./workOrders";

/** What the domain's own table allows from a status, when the platform has not said. */
const ALL_STATUSES: readonly WorkOrderStatus[] = [...OPEN_STATUSES, ...TERMINAL_STATUSES];
const TRANSITIONS_FROM = (from: WorkOrderStatus): WorkOrderStatus[] =>
  ALL_STATUSES.filter((to) => canTransition(from, to));
import {
  daysUntil,
  expectedAt,
  partsFor,
  type StoresRegister,
  type WorkOrderParts,
} from "./stores";

/** The marker a parts hold's note carries, so the pause can be found again. */
export const PARTS_MARKER = "[parts]";

/**
 * The note a parts hold is recorded with.
 *
 * It names the parts, because "on hold" with no object is the status that
 * teaches a supervisor to stop reading statuses.
 */
export function partsHoldNote(parts: WorkOrderParts): string {
  const short = parts.lines.filter((l) => l.outstanding > 0 && l.availableNow < l.outstanding);
  const names = short.map((l) => `${l.reservation.sku} ×${l.outstanding - l.availableNow}`).join(", ");
  const when = parts.blocked
    ? t("nothing is on order")
    : parts.readyAt
      ? t("due {when}", { when: parts.readyAt })
      : t("no delivery date");
  return `${PARTS_MARKER} ${t("Waiting for parts: {names} — {when}.", { names, when })}`;
}

/** Was this transition a hold for parts, rather than for anything else? */
export function isPartsHold(event: WorkOrderEvent): boolean {
  return event.to === "on_hold" && (event.note ?? "").includes(PARTS_MARKER);
}

/**
 * How long this job has been held for PARTS, from its own history.
 *
 * Counted between each parts hold and the transition that ended it, plus the
 * time since the last one if the job is still held. A hold entered for any
 * other reason contributes nothing, which is the point.
 */
export function heldForPartsMs(history: readonly WorkOrderEvent[], status: WorkOrderStatus, now: Date): number {
  const ordered = [...history].sort((a, b) => a.at.localeCompare(b.at));
  let total = 0;
  let since: number | null = null;
  for (const e of ordered) {
    const at = Date.parse(e.at);
    if (Number.isNaN(at)) continue;
    if (isPartsHold(e)) {
      /* a second parts hold while already held changes nothing */
      since = since ?? at;
    } else if (since !== null && e.to !== "on_hold") {
      total += at - since;
      since = null;
    }
  }
  /* still held, and still for parts: the clock is paused now */
  if (since !== null && status === "on_hold") total += now.getTime() - since;
  return Math.max(0, total);
}

/** Days, rounded, for a sentence. */
export function heldForPartsDays(history: readonly WorkOrderEvent[], status: WorkOrderStatus, now: Date): number {
  return Math.floor(heldForPartsMs(history, status, now) / 86_400_000);
}

/* ── what the job should do about it ──────────────────────────────────── */

export type HoldAdvice =
  | { action: "hold"; why: string }
  | { action: "release"; why: string }
  /** short, but the job's current status cannot be held — say so rather than offer it */
  | { action: "cannot"; why: string }
  | { action: "none"; why: string };

/**
 * Whether this job should go on hold for parts, or come off.
 *
 * It advises; it never acts. The platform owns the transition and refuses
 * what its own rules refuse, and a screen that moved a job's status because
 * a shelf was empty would be making a maintenance decision from the stores.
 */
export function holdAdvice(
  parts: WorkOrderParts,
  status: WorkOrderStatus,
  heldForParts: boolean,
  /** what the platform says this job may do next; the domain's table when unknown */
  allowed: readonly WorkOrderStatus[] = TRANSITIONS_FROM(status),
): HoldAdvice {
  const short = parts.short > 0;
  if (short && status !== "on_hold" && status !== "completed" && status !== "verified" && status !== "cancelled") {
    /*
     * The platform has the final word on a move, so a hold is offered only
     * where it is legal. A draft job cannot be held — it has to be approved
     * first — and a button that looked like it could be pressed would be a
     * refusal waiting to happen.
     */
    if (!allowed.includes("on_hold")) {
      return {
        action: "cannot",
        why: t(
          "This job is short of a part, but a job that is {status} cannot be put on hold. Until it is, the wait counts against its service level.",
          { status: t(STATUS_LABEL[status]) },
        ),
      };
    }
    return {
      action: "hold",
      why: parts.blocked
        ? tn(
            parts.short,
            "{n} part is short and nothing is on order. Time spent waiting will count against this job's service level unless it is held.",
            "{n} parts are short and nothing is on order. Time spent waiting will count against this job's service level unless it is held.",
          )
        : tn(
            parts.short,
            "{n} part is short. Time spent waiting will count against this job's service level unless it is held.",
            "{n} parts are short. Time spent waiting will count against this job's service level unless it is held.",
          ),
    };
  }
  if (!short && status === "on_hold" && heldForParts && allowed.length > 0) {
    return {
      action: "release",
      why: t("Every part this job was waiting for is on the shelf. Nothing is holding it now."),
    };
  }
  return { action: "none", why: "" };
}

/* ── the timeline ─────────────────────────────────────────────────────── */

export interface PartsStep {
  key: string;
  /** when it happened, or when it is due; absent when neither is known */
  at?: string;
  what: string;
  detail?: string;
  state: "done" | "due" | "late" | "waiting";
}

/**
 * What has happened to this job's parts, and what is still to come.
 *
 * Assembled from the register's own records rather than from a status field:
 * the reservation's date, the requisition that asked for it, the order that
 * was raised, what the supplier promised, what arrived, what was issued. A
 * timeline a person has to maintain by hand is a timeline that is wrong.
 */
export function partsTimeline(workOrder: string, register: StoresRegister, now: Date): PartsStep[] {
  const parts = partsFor(workOrder, register, now);
  if (parts.lines.length === 0) return [];
  const steps: PartsStep[] = [];

  const reservedAt = parts.lines.map((l) => l.reservation.at).sort()[0];
  steps.push({
    key: "reserved",
    at: reservedAt,
    what: t("Parts reserved for this job"),
    detail: parts.lines.map((l) => `${l.reservation.sku} ×${l.reservation.qty}`).join(", "),
    state: "done",
  });

  const shortLines = parts.lines.filter((l) => l.outstanding > 0 && l.availableNow < l.outstanding);
  if (shortLines.length > 0) {
    steps.push({
      key: "short",
      what: t("The shelf could not fill it"),
      detail: shortLines
        .map((l) => t("{sku}: {n} short", { sku: l.reservation.sku, n: l.outstanding - l.availableNow }))
        .join(", "),
      state: "done",
    });
  }

  /* what was asked for, and by whom */
  const skus = new Set(parts.lines.map((l) => l.reservation.sku));
  const asked = register.requisitions.filter((q) => q.lines.some((l) => skus.has(l.sku)));
  for (const q of asked) {
    steps.push({
      key: `rq-${q.number}`,
      at: q.raisedAt,
      what: t("Requisition {number} raised", { number: q.number }),
      detail:
        q.status === "submitted"
          ? t("waiting on a decision")
          : q.status === "approved"
            ? t("approved by {who}", { who: q.approvals.at(-1)?.by ?? "" })
            : q.status === "ordered"
              ? t("became {number}", { number: q.orderNumber ?? "" })
              : t(q.status),
      state: q.status === "submitted" ? "waiting" : "done",
    });
  }

  /* what is on the way */
  for (const o of register.orders) {
    const line = o.lines.find((l) => skus.has(l.sku));
    if (!line) continue;
    if (o.status === "cancelled") continue;
    const owing = line.qty - line.received;
    const late = (daysUntil(o.expectedAt, now) ?? 1) < 0 && owing > 0;
    steps.push({
      key: `po-${o.number}`,
      at: o.raisedAt,
      what: t("Ordered on {number} from {supplier}", { number: o.number, supplier: o.supplier }),
      detail: o.raisedElsewhere ? t("raised in {system}", { system: o.raisedElsewhere.system }) : undefined,
      state: "done",
    });
    if (owing > 0) {
      steps.push({
        key: `due-${o.number}`,
        at: o.expectedAt,
        what: late ? t("The delivery is late") : t("Delivery expected"),
        detail: t("{n} outstanding on {number}", { n: owing, number: o.number }),
        state: late ? "late" : "due",
      });
    }
    for (const r of o.receipts) {
      if (!r.lines.some((l) => skus.has(l.sku))) continue;
      steps.push({
        key: `rcv-${o.number}-${r.at}`,
        at: r.at,
        what: t("Delivered and booked in"),
        detail: r.lines
          .filter((l) => skus.has(l.sku))
          .map((l) => `${l.sku} ×${l.qty}`)
          .join(", "),
        state: "done",
      });
    }
  }

  /* what reached the technician */
  for (const m of register.movements) {
    if (m.workOrder !== workOrder || m.kind !== "issue") continue;
    steps.push({
      key: `iss-${m.id}`,
      at: m.at,
      what: t("Issued to the job"),
      detail: `${m.sku} ×${Math.abs(m.qty)}`,
      state: "done",
    });
  }

  if (parts.complete) {
    steps.push({ key: "ready", what: t("Every part is with the job"), state: "done" });
  }

  return steps.sort((a, b) => (a.at ?? "9999").localeCompare(b.at ?? "9999"));
}

/* ── telling whoever is doing the work ────────────────────────────────── */

/**
 * The message that goes out when the parts land.
 *
 * Written here rather than on the screen so it says the same thing every
 * time, and so it can be tested. It names the job, the parts, where they are
 * and who to ask for — everything somebody needs to turn up and start, with
 * nothing they have to look up first.
 */
export function partsReadyMessage(
  wo: Pick<WorkOrder, "number" | "title" | "crew">,
  register: StoresRegister,
  now: Date,
  storeName: (code: string) => string,
): string {
  const parts = partsFor(wo.number, register, now);
  const lines = parts.lines
    .filter((l) => l.outstanding > 0)
    .map((l) => {
      const item = register.items.find((i) => i.sku === l.reservation.sku);
      const where = item?.bins.find((b) => b.onHand > 0)?.location;
      return `  · ${l.reservation.sku} ×${l.outstanding}${item ? ` ${item.name}` : ""}${where ? ` — ${storeName(where)}` : ""}`;
    });
  return [
    t("{number}: the parts you were waiting for are in.", { number: wo.number }),
    "",
    wo.title,
    "",
    lines.length ? t("Held for you at the stores:") : t("Everything for this job has already been issued."),
    ...lines,
    "",
    t("Collect them at the counter against {number}. The job comes off hold when work restarts.", {
      number: wo.number,
    }),
  ].join("\n");
}

/** Who the message is for, named rather than guessed. */
export function messageTo(wo: Pick<WorkOrder, "crew">): string {
  return wo.crew?.trim() || t("whoever is doing the work");
}

/**
 * A message that was composed and recorded. The platform sends nothing — it
 * has no outbound channel — so what is honest is to record that somebody was
 * told, by whom and when, and let them send it the way they already do.
 */
export interface PartsAdvice {
  id: string;
  at: string;
  by: string;
  workOrder: string;
  to: string;
  message: string;
}

/** The soonest any outstanding part for this job is expected; null when none is. */
export function jobReadyAt(workOrder: string, register: StoresRegister, now: Date): string | null {
  const parts = partsFor(workOrder, register, now);
  const dates = parts.lines
    .filter((l) => l.outstanding > 0)
    .map((l) => expectedAt(l.reservation.sku, register))
    .filter((d): d is string => Boolean(d));
  return dates.length ? dates.sort().at(-1) ?? null : null;
}
